import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AudioLines, BookHeart, Coins, CupSoda, Droplets, Flame, Flower2, GlassWater, ScrollText, Sparkles, Volume2, VolumeX, Wheat } from "lucide-react";
import { useSanctumStore } from "../stores/useSanctumStore";
import { generateNovena, reflectOnPetition } from "../services/lunaPipeline";
import { audioEngine } from "../services/audioEngine";
import type { Offering, OfferingType, Palette } from "../types";
import { ShrineCanvas } from "./ShrineCanvas";

const PALETTES: Array<{ id: Palette; label: string; meaning: string; color: string }> = [
  { id: "blanca", label: "Blanca", meaning: "Purification", color: "#e8dfc8" },
  { id: "roja", label: "Roja", meaning: "Love", color: "#aa3040" },
  { id: "dorada", label: "Dorada", meaning: "Prosperity", color: "#c99a3d" },
  { id: "negra", label: "Negra", meaning: "Protection", color: "#312d35" },
  { id: "verde", label: "Verde", meaning: "Justice", color: "#477653" },
  { id: "morada", label: "Morada", meaning: "Wisdom", color: "#765387" },
  { id: "siete", label: "7 Potencias", meaning: "Whole spectrum", color: "linear-gradient(90deg,#eee,#a33,#ca4,#486,#758)" },
];
const OFFERINGS: Array<{ id: OfferingType; label: string; Icon: typeof Droplets }> = [
  { id: "water", label: "Fresh water", Icon: Droplets }, { id: "candle", label: "Veladora", Icon: Flame },
  { id: "incense", label: "Copal", Icon: AudioLines }, { id: "bread", label: "Pan de Muerto", Icon: Wheat },
  { id: "marigolds", label: "Marigolds", Icon: Flower2 }, { id: "tequila", label: "Tequila", Icon: GlassWater },
  { id: "cigar", label: "Cigar", Icon: ScrollText }, { id: "coins", label: "Coins", Icon: Coins },
];

function prayerText(prayer: Awaited<ReturnType<typeof generateNovena>>["result"]) {
  return [prayer.title, prayer.opening, prayer.petition, prayer.refrain, ...prayer.days.map((day, index) => `Day ${index + 1}: ${day}`), prayer.closing].join("\n\n");
}

export function ShrineModule() {
  const shrine = useSanctumStore((state) => state.shrine);
  const setPalette = useSanctumStore((state) => state.setPalette);
  const addOffering = useSanctumStore((state) => state.addOffering);
  const updateOffering = useSanctumStore((state) => state.updateOffering);
  const addPetition = useSanctumStore((state) => state.addPetition);
  const enrichPetition = useSanctumStore((state) => state.enrichPetition);
  const burnPetition = useSanctumStore((state) => state.burnPetition);
  const setAudio = useSanctumStore((state) => state.setAudio);
  const [petition, setPetition] = useState("");
  const [title, setTitle] = useState("");
  const [working, setWorking] = useState<string | null>(null);
  const [burning, setBurning] = useState<string | null>(null);
  const [notice, setNotice] = useState("Offerings rest on the lower altar. Click one to tend it.");
  const freshWaterAge = useMemo(() => shrine.offerings.filter((item) => item.type === "water" && Date.now() > item.expiresAt).length, [shrine.offerings]);

  const handleOffering = (item: Offering) => {
    if (item.type === "water") { updateOffering(item.id, "refill"); setNotice("The glass has been cleansed and refilled."); }
    else if (item.type === "candle") { updateOffering(item.id, "light"); audioEngine.matchStrike(); setNotice("The veladora will burn for four real-world hours."); }
    else if (item.type === "cigar") { updateOffering(item.id, "puff"); audioEngine.cigarPuff(); setNotice("A small curl of smoke rises from the offering."); }
    else { updateOffering(item.id, "remove"); setNotice("The offering has been respectfully cleared."); }
  };

  const createPetition = () => {
    if (!petition.trim()) return;
    addPetition(title, petition);
    setPetition(""); setTitle("");
  };

  const askCompanion = async (petitionId: string, text: string) => {
    setWorking(petitionId);
    const [reflection, prayer] = await Promise.all([reflectOnPetition(text), generateNovena(text)]);
    enrichPetition(petitionId, {
      reflection: `${reflection.result.reflection}\n\nSuggested offerings: ${reflection.result.offerings.join(", ")}. ${reflection.result.note}`,
      prayer: prayerText(prayer.result), palette: reflection.result.palette,
    });
    setNotice(reflection.offline || prayer.offline ? "The local companion prepared a private structure; the Luna signal was unavailable." : "The Luna companion returned a private devotional structure.");
    setWorking(null);
  };

  const toggleAudio = () => {
    const enabled = !shrine.audio.enabled;
    setAudio({ enabled });
    if (enabled) audioEngine.start(shrine.audio); else audioEngine.stopAmbient();
  };

  return <div className="module-grid">
    <section className="space-y-5">
      <div className="panel overflow-hidden p-1 shadow-ember">
        <ShrineCanvas palette={shrine.palette} offerings={shrine.offerings} onPlace={addOffering} onOfferingClick={handleOffering} />
        <div className="flex items-center justify-between gap-4 px-4 py-3 text-xs text-bone/55">
          <span>{notice}</span>
          {freshWaterAge > 0 && <span className="text-amber-300">{freshWaterAge} glass{freshWaterAge > 1 ? "es" : ""} need refreshing</span>}
        </div>
      </div>

      <div className="panel p-5">
        <div className="eyebrow">Cowl palette</div>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {PALETTES.map((item) => <button key={item.id} onClick={() => setPalette(item.id)} className={`palette-button ${shrine.palette === item.id ? "active" : ""}`}>
            <span className="h-5 w-5 rounded-full border border-white/20" style={{ background: item.color }} />
            <span><b>{item.label}</b><small>{item.meaning}</small></span>
          </button>)}
        </div>
      </div>

      <div className="panel p-5">
        <div className="flex items-end justify-between gap-4"><div><div className="eyebrow">Offering tray</div><p className="mt-1 text-sm text-bone/55">Click to place, or drag onto the altar.</p></div><Sparkles className="text-brass" size={18} /></div>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {OFFERINGS.map(({ id: offeringId, label, Icon }) => <button key={offeringId} draggable
            onDragStart={(event) => event.dataTransfer.setData("application/x-offering", offeringId)}
            onClick={() => addOffering(offeringId)} className="offering-button"><Icon size={17} /><span>{label}</span></button>)}
        </div>
      </div>
    </section>

    <aside className="space-y-5">
      <div className="panel p-5">
        <div className="flex items-center justify-between"><div className="eyebrow">Ambient room</div><button className="icon-button" aria-label={shrine.audio.enabled ? "Mute ambient room" : "Enable ambient room"} onClick={toggleAudio}>{shrine.audio.enabled ? <Volume2 size={18} /> : <VolumeX size={18} />}</button></div>
        <p className="mt-3 text-sm leading-relaxed text-bone/55">Procedural wick crackle, copal hum, and a low drone. Nothing leaves your browser.</p>
        <input aria-label="Ambient volume" type="range" min="0" max="1" step=".05" value={shrine.audio.master} onChange={(event) => { const master = Number(event.target.value); setAudio({ master }); audioEngine.update({ ...shrine.audio, master }); }} className="mt-4 w-full accent-brass" />
      </div>

      <div className="panel p-5">
        <div className="flex items-center gap-2"><BookHeart size={17} className="text-brass"/><div className="eyebrow">Private petition</div></div>
        <input className="field mt-4" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="A private title (optional)" maxLength={100} />
        <textarea className="field mt-3 min-h-36 resize-y" value={petition} onChange={(event) => setPetition(event.target.value)} placeholder="Write plainly. Unsigned text stays in memory only; sign in to encrypt and sync it." maxLength={4000} />
        <button className="primary-button mt-3 w-full" onClick={createPetition} disabled={!petition.trim()}>Seal petition</button>
      </div>

      <div className="space-y-3">
        <AnimatePresence initial={false}>
          {shrine.petitions.slice().reverse().map((item) => <motion.article key={item.id} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: burning === item.id ? 0 : 1, y: burning === item.id ? -45 : 0, filter: burning === item.id ? "blur(5px) sepia(1)" : "none" }} exit={{ opacity: 0, scale: .94 }} className="panel p-5">
            <div className="flex items-start justify-between gap-4"><div><h3 className="font-display text-lg text-bone">{item.title}</h3><time className="text-[10px] uppercase tracking-[.18em] text-bone/35">{new Date(item.createdAt).toLocaleDateString()}</time></div><span className="h-3 w-3 rounded-full" style={{ background: PALETTES.find((entry) => entry.id === item.palette)?.color }} /></div>
            <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-bone/65">{item.text}</p>
            {item.reflection && <div className="mt-4 border-l border-brass/40 pl-3 text-xs leading-5 text-bone/55 whitespace-pre-wrap">{item.reflection}</div>}
            {item.prayer && <details className="mt-4 text-sm text-bone/65"><summary className="cursor-pointer text-brass">Open nine-day prayer</summary><div className="mt-3 max-h-64 overflow-y-auto whitespace-pre-wrap pr-2 leading-6">{item.prayer}</div></details>}
            <div className="mt-4 flex flex-wrap gap-2">
              <button className="secondary-button" onClick={() => askCompanion(item.id, item.text)} disabled={working === item.id}>{working === item.id ? "Listening…" : "Ask companion"}</button>
              {item.palette !== shrine.palette && <button className="secondary-button" onClick={() => setPalette(item.palette)}>Wear suggested color</button>}
              <button className="danger-button" onClick={() => { setBurning(item.id); audioEngine.paperBurn(); window.setTimeout(() => { burnPetition(item.id); setBurning(null); }, 900); }}><Flame size={14}/> Burn & archive</button>
            </div>
          </motion.article>)}
        </AnimatePresence>
        {!shrine.petitions.length && <div className="empty-state"><CupSoda size={25}/><p>No active petitions. The altar can remain quiet.</p></div>}
      </div>
    </aside>
  </div>;
}
